/**
 * App — router only. No business logic per MONOREPO.md §4.1.
 *
 * Phase-1b expands the citizen surface to include the design-system landing
 * page, customer auth screens, and the authenticated customer portal.
 *
 * Route plan:
 *   - `/`                        → LandingPage (full-bleed, no PublicLayout chrome)
 *   - `/auth/login`              → CustomerLoginPage (full-bleed)
 *   - `/auth/register`           → RegisterPage (full-bleed)
 *   - `/auth/forgot-password`    → ForgotPasswordPage (full-bleed)
 *   - `/legal/privacy`           → PrivacyPolicyPage (LegalLayout, public)
 *   - `/legal/terms`             → TermsAndConditionsPage (LegalLayout, public)
 *   - `/track`                   → TrackRoute (PublicLayout chrome)
 *   - `/track/:trackingNumber`   → TrackingRoute (PublicLayout chrome)
 *   - `/portal/*`                → CustomerLayout (auth-gated)
 *       - Phase 1b: dashboard / shipments / tracking / profile / notifications
 *       - Phase 1c: parcel-booking, drafts, complaints/new, address-change,
 *                   reschedule, pickup, outlets, topup, preferences, settings,
 *                   register
 *   - `*`                        → NotFoundRoute
 */
import type { JSX } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { CustomerLoginPage } from './features/auth/CustomerLoginPage';
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage';
import { RegisterPage } from './features/auth/RegisterPage';
import { HelpPage } from './features/help/HelpPage';
import { LandingPage } from './features/landing/LandingPage';
import { PrivacyPolicyPage } from './features/legal/PrivacyPolicyPage';
import { TermsAndConditionsPage } from './features/legal/TermsAndConditionsPage';
import { AirtimeTopUpPage } from './features/nipost-pay/AirtimeTopUpPage';
import { BillPaymentPage } from './features/nipost-pay/BillPaymentPage';
import { CashInPage as NipostPayCashInPage } from './features/nipost-pay/CashInPage';
import { CashOutPage as NipostPayCashOutPage } from './features/nipost-pay/CashOutPage';
import { DebitCardPage } from './features/nipost-pay/DebitCardPage';
import { NipostPayDashboardPage } from './features/nipost-pay/NipostPayDashboardPage';
import { NipostPayLayout } from './features/nipost-pay/NipostPayLayout';
import { P2PTransferPage } from './features/nipost-pay/P2PTransferPage';
import { SocialDisbursementPage } from './features/nipost-pay/SocialDisbursementPage';
import { WalletKYCTiersPage } from './features/nipost-pay/WalletKYCTiersPage';
import { WalletOnboardingPage } from './features/nipost-pay/WalletOnboardingPage';
import { AddressModificationPage } from './features/portal/AddressModificationPage';
import { ComplaintSubmissionPage } from './features/portal/ComplaintSubmissionPage';
import { CustomerComplaintsListPage } from './features/portal/CustomerComplaintsListPage';
import { CustomerDashboard } from './features/portal/CustomerDashboard';
import { CustomerDraftsPage } from './features/portal/CustomerDraftsPage';
import { CustomerNotifications } from './features/portal/CustomerNotifications';
import { CustomerProfile } from './features/portal/CustomerProfile';
import { CustomerRegistrationPage } from './features/portal/CustomerRegistrationPage';
import { CustomerShipments } from './features/portal/CustomerShipments';
import { CustomerTopUpPage } from './features/portal/CustomerTopUpPage';
import { CustomerTracking } from './features/portal/CustomerTracking';
import { DeliveryPreferencesPage } from './features/portal/DeliveryPreferencesPage';
import { DeliveryReschedulingPage } from './features/portal/DeliveryReschedulingPage';
import { LanguageAccessibilityPage } from './features/portal/LanguageAccessibilityPage';
import { OutletLocatorPage } from './features/portal/OutletLocatorPage';
import { ParcelBookingPage } from './features/portal/ParcelBookingPage';
import { PickupLocationSelectionPage } from './features/portal/PickupLocationSelectionPage';
import { CustomerLayout } from './layouts/CustomerLayout';
import { PublicLayout } from './layouts/PublicLayout';
import { RequireCustomerAuth } from './lib/auth/RequireCustomerAuth';
import { NotFoundRoute } from './routes/NotFound.route';
import { TrackRoute } from './routes/Track.route';
import { TrackingRoute } from './routes/Tracking.route';

export function App(): JSX.Element {
  return (
    <Routes>
      {/* Full-bleed routes — no shared chrome. */}
      <Route index element={<LandingPage />} />
      <Route path="auth/login" element={<CustomerLoginPage />} />
      <Route path="auth/register" element={<RegisterPage />} />
      <Route path="auth/forgot-password" element={<ForgotPasswordPage />} />

      {/* Public legal pages — own chrome (LegalLayout); not auth-gated. */}
      <Route path="legal/privacy" element={<PrivacyPolicyPage />} />
      <Route path="legal/terms" element={<TermsAndConditionsPage />} />

      {/* Authenticated customer portal — its own layout chrome. */}
      <Route
        path="portal"
        element={
          <RequireCustomerAuth>
            <CustomerLayout />
          </RequireCustomerAuth>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<CustomerDashboard />} />
        <Route path="shipments" element={<CustomerShipments />} />
        <Route path="tracking" element={<CustomerTracking />} />
        <Route path="profile" element={<CustomerProfile />} />
        <Route path="notifications" element={<CustomerNotifications />} />
        <Route path="parcel-booking" element={<ParcelBookingPage />} />
        <Route path="drafts" element={<CustomerDraftsPage />} />
        <Route path="complaints" element={<CustomerComplaintsListPage />} />
        <Route path="complaints/new" element={<ComplaintSubmissionPage />} />
        <Route path="address-change" element={<AddressModificationPage />} />
        <Route path="reschedule" element={<DeliveryReschedulingPage />} />
        <Route path="pickup" element={<PickupLocationSelectionPage />} />
        <Route path="outlets" element={<OutletLocatorPage />} />
        <Route path="topup" element={<CustomerTopUpPage />} />
        <Route path="preferences" element={<DeliveryPreferencesPage />} />
        <Route path="settings" element={<LanguageAccessibilityPage />} />
        <Route path="register" element={<CustomerRegistrationPage />} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Route>

      {/* NIPOST Pay subtree — separate chrome, auth-gated. */}
      <Route
        path="nipost-pay"
        element={
          <RequireCustomerAuth>
            <NipostPayLayout />
          </RequireCustomerAuth>
        }
      >
        <Route index element={<NipostPayDashboardPage />} />
        <Route path="onboarding"     element={<WalletOnboardingPage />} />
        <Route path="kyc"            element={<WalletKYCTiersPage />} />
        <Route path="cash-in"        element={<NipostPayCashInPage />} />
        <Route path="cash-out"       element={<NipostPayCashOutPage />} />
        <Route path="transfer"       element={<P2PTransferPage />} />
        <Route path="airtime"        element={<AirtimeTopUpPage />} />
        <Route path="bill-pay"       element={<BillPaymentPage />} />
        <Route path="card"           element={<DebitCardPage />} />
        <Route path="disbursements"  element={<SocialDisbursementPage />} />
        <Route path="*" element={<Navigate to="/nipost-pay" replace />} />
      </Route>

      {/* Public tracking + help + 404 — share PublicLayout chrome. */}
      <Route element={<PublicLayout />}>
        <Route path="track" element={<TrackRoute />} />
        <Route path="track/:trackingNumber" element={<TrackingRoute />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="*" element={<NotFoundRoute />} />
      </Route>
    </Routes>
  );
}
